import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { debounce } from 'lodash'

export default function useList() {
  const { t } = useI18nUtils()
  const trans = {
    'messages.error.server_error': t('messages.error.server_error'),
  }

  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    { label: 'actions.name', key: 'actions', sortable: false },
    { label: 'patrol_logs.unit_commander.name', key: 'name', sortable: true },
    { label: 'patrol_logs.unit_commander.telephone_number', key: 'telephone_number', sortable: false },
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const filters = ref({})
  const filtersCount = useFilterCounter(filters)

  const buildListQueryParams = () => ({
    q: searchQuery.value,
    max: perPage.value,
    'page[n]': currentPage.value,
    sortBy: sortBy.value,
    sortDesc: isSortDirDesc.value,
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('unit_commander/getAll', buildListQueryParams())
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.error.server_error'],
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const buildFilters = () => {
    refetchData()
  }

  const clearSearchFilters = () => {
    filters.value = {}
    buildFilters()
  }

  return {
    refListTable,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    totalList,
    dataMeta,
    fetchList,
    refetchData,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    buildListQueryParams,
  }
}
